import { motion } from 'framer-motion';
import { Moon, Sun } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useDashboard } from '@/hooks/useDashboard';
import { KPICards } from '@/components/KPICards';
import { WinnerBanner } from '@/components/WinnerBanner';
import { ParametersPanel } from '@/components/ParametersPanel';
import { NetCostChart, GrossCostByProductChart } from '@/components/Charts';
import { ProductsTable } from '@/components/ProductsTable';
import { SupplierDataPanel } from '@/components/SupplierDataPanel';
import { SupplierReport } from '@/components/SupplierReport';
import { IVAModule } from '@/components/IVAModule';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';

export default function Dashboard() {
  const { theme, setTheme } = useTheme();
  const {
    products,
    p1Tariffs,
    p2Tariffs,
    parameters,
    grossP1,
    grossP2,
    billingBenefit,
    netP1,
    netP2,
    savingsEur,
    savingsPct,
    p1Contribution,
    p2Contribution,
    p1RappelAmount,
    p2RappelAmount,
    winnerNetCost,
    vatAmount,
    totalWithVat,
    loserNetCost,
    loserTotalWithVat,
    winnerName,
    updateProduct,
    deleteProduct,
    addProduct,
    updateTariff,
    updateParameter
  } = useDashboard();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-background"
    >
      <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto max-w-7xl px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight text-foreground">
                Dashboard Comparativo de Aprovisionamiento 2026
              </h1>
              <Badge variant="secondary" className="bg-accent text-accent-foreground">
                2026
              </Badge>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              className="rounded-full"
            >
              {theme === 'dark' ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto max-w-7xl px-4 py-8 space-y-10">
        <WinnerBanner
          netP1={netP1}
          netP2={netP2}
          savingsPct={savingsPct}
          savingsEur={savingsEur}
          p1Contribution={p1Contribution}
          p2Contribution={p2Contribution}
          furnitureCost={parameters.furnitureCost}
          billingBenefit={billingBenefit}
        />

        <KPICards
          grossP1={grossP1}
          grossP2={grossP2}
          netP1={netP1}
          netP2={netP2}
          savingsPct={savingsPct}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <ParametersPanel
            params={parameters}
            onUpdate={updateParameter}
            p2Contribution={p2Contribution}
            billingBenefit={billingBenefit}
          />
          <Card className="p-6">
            <NetCostChart netP1={netP1} netP2={netP2} />
          </Card>
        </div>

        <section>
          <h2 className="text-2xl font-semibold tracking-tight mb-6">Coste Bruto por Producto</h2>
          <GrossCostByProductChart products={products} />
        </section>

        <section>
          <h2 className="text-2xl font-semibold tracking-tight mb-6">Análisis de Productos</h2>
          <ProductsTable
            products={products}
            p1Tariffs={p1Tariffs}
            p2Tariffs={p2Tariffs}
            onUpdateProduct={updateProduct}
            onDeleteProduct={deleteProduct}
            onAddProduct={addProduct}
          />
        </section>

        <section>
          <h2 className="text-2xl font-semibold tracking-tight mb-6">Datos de Proveedores</h2>
          <SupplierDataPanel
            p1Tariffs={p1Tariffs}
            p2Tariffs={p2Tariffs}
            onUpdateTariff={updateTariff}
          />
        </section>

        <section>
          <h2 className="text-2xl font-semibold tracking-tight mb-6">Informe Comparativo</h2>
          <SupplierReport
            grossP1={grossP1}
            grossP2={grossP2}
            netP1={netP1}
            netP2={netP2}
            p1Contribution={p1Contribution}
            p2Contribution={p2Contribution}
            furnitureCost={parameters.furnitureCost}
            includeFurniture={parameters.includeFurniture}
            savingsPct={savingsPct}
            savingsEur={savingsEur}
            litros={parameters.p2LitresSold}
            eurPerLitro={parameters.p2EurPerLitre}
            billingBenefit={billingBenefit}
            p1RappelAmount={p1RappelAmount}
            p2RappelAmount={p2RappelAmount}
          />
        </section>

        <section>
          <h2 className="text-2xl font-semibold tracking-tight mb-6">Rentabilidad con IVA</h2>
          <IVAModule
            winnerName={winnerName}
            winnerNetCost={winnerNetCost}
            loserNetCost={loserNetCost}
            vatRate={parameters.vatRate}
            vatAmount={vatAmount}
            totalWithVat={totalWithVat}
            loserTotalWithVat={loserTotalWithVat}
          />
        </section>
      </main>
    </motion.div>
  );
}